/**
 * Chrome placement (04 Stage and chrome, plan §3.2): the transport bar always overlays the bottom
 * edge of the stage on a translucent plate: visible while paused, on mouse move, or with the
 * pointer within 80 stage px of the bottom edge; fades out 2.0 s after the last move while
 * playing. On touch, a tap on the map that hits nothing else toggles the bar (plan T2; portrait
 * sets `tapTogglesChrome` false — its transport is always visible).
 * - The bar is 72·k device px tall (k = f); `--s` (CSS px per stage px) sizes the buttons inside.
 * - `--font-l/m/s` (CSS px) are the text tiers' glyph heights at the snapped size, so DOM text
 *   steps with the pixel text (core/hud/type.ts).
 * - `H` toggles the chrome off entirely.
 * - Portrait (r14 P8): the bar is the bottom sheet's top row, 44·k device px at the sheet's top, always
 *   shown (no auto-hide); `data-portrait` on `#app` switches the bar's CSS to its portrait row.
 */
import { glyphHeight } from '../core/hud/type.ts';
import { stageDeviceSize } from '../core/layout/scale.ts';
import type { StageLayout } from './stage.ts';

/**
 * Cap height of the shell's `ui-monospace` stack as a fraction of its font-size, measured in
 * Chromium (canvas `measureText('H').actualBoundingBoxAscent / size` = 0.72; SF Mono 0.70, Menlo
 * 0.73, Consolas 0.64). The tier vars divide by it so DOM capitals are as tall as the HUD's pixel
 * text of the same tier.
 */
export const MONO_CAP_HEIGHT = 0.72;

/** Stage width below which the transport hides its LAP readout. */
export const NARROW_STAGE_W = 1500;

/** Publish --font-l/m/s (CSS px; tier glyph height / cap ratio) on <html>: the overlays sit outside #app. */
export function publishFontVars(k: number, dpr: number): void {
  const rs = document.documentElement.style;
  for (const t of ['L', 'M', 'S'] as const) {
    rs.setProperty(`--font-${t.toLowerCase()}`, `${String(glyphHeight(t, k) / dpr / MONO_CAP_HEIGHT)}px`);
  }
}

export const AUTO_HIDE_MS = 2000;
export const EDGE_ZONE_STAGE_PX = 80;
/** Transport bar height in stage px. */
export const CHROME_BAR_STAGE_PX = 72;
/** The portrait transport row in stage px (the view's `P_TRANSPORT_H`). */
export const SHEET_BAR_STAGE_PX = 44;
/** The positions band's height in stage px (= undercut layout BAND_H0, kept here so the shell does not load the view code). */
export const BAND_STAGE_PX = 275;

export class ChromeController {
  private lastMove = -Infinity;
  private nearEdge = false;
  private hidden = false;
  private visible = true;
  private hold = 0;
  private stageH = 1080;
  private portrait = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly bar: HTMLElement,
  ) {
    bar.addEventListener('pointerenter', () => {
      this.hold++;
    });
    bar.addEventListener('pointerleave', () => {
      this.hold = Math.max(0, this.hold - 1);
    });
  }

  get isVisible(): boolean {
    return this.visible && !this.hidden;
  }

  /** Place the bar for a stage layout (device px → CSS px) and publish the CSS scale variables;
   *  `sheetTop` (stage px) puts it at the top of the portrait sheet instead of the stage's bottom edge. */
  place(l: StageLayout, sheetTop: number | null = null): void {
    const r = l.resolved;
    const k = l.scale.k;
    const dpr = l.dpr;
    const dev = stageDeviceSize(r);
    this.stageH = r.stage.h;
    this.root.style.setProperty('--s', String(k / dpr));
    // the stage's top-left corner (CSS px), for the SWITCH RACE button
    this.root.style.setProperty('--stage-x', `${String(r.offset.x / dpr)}px`);
    this.root.style.setProperty('--stage-y', `${String(r.offset.y / dpr)}px`);
    // the top of the positions band (CSS px from the top), on <html>: the start screen's race picker sits above it
    document.documentElement.style.setProperty('--band-top', `${String((r.offset.y + (r.stage.h - BAND_STAGE_PX) * k) / dpr)}px`);
    // a 4:3 stage (1440 wide) cannot hold every readout: the transport drops the duplicate LAP readout (the HUD header shows it)
    this.root.dataset.narrow = r.stage.w < NARROW_STAGE_W ? '1' : '0';
    // on <html>: the loading card, help, debug and gate overlays sit outside #app
    publishFontVars(k, dpr);
    this.portrait = sheetTop !== null;
    this.root.dataset.portrait = this.portrait ? '1' : '0';
    const botH = (this.portrait ? SHEET_BAR_STAGE_PX : CHROME_BAR_STAGE_PX) * k;
    const b = this.bar.style;
    b.left = `${String(r.offset.x / dpr)}px`;
    b.width = `${String(dev.w / dpr)}px`;
    if (sheetTop === null) {
      // HPF1: the bar GROWS by the safe-area inset instead of shrinking: #transport is
      // box-sizing: border-box with padding-bottom env(safe-area-inset-bottom), so a plain botH
      // height let the inset eat the content box (48 − 34 = 14 CSS px on an iPhone). The content box
      // stays botH/dpr; the total adds the inset (0 on the desktop → identical layout).
      b.height = `calc(${String(botH / dpr)}px + env(safe-area-inset-bottom, 0px))`;
      b.top = `${String((r.offset.y + dev.h - botH) / dpr)}px`;
    } else {
      // portrait: the sheet's top row (the safe-area inset is at the sheet's bottom, under the attribution)
      b.height = `${String(botH / dpr)}px`;
      b.top = `${String((r.offset.y + sheetTop * k) / dpr)}px`;
    }
    // portrait: the transport is always shown, so a tap on the map never toggles it (T2)
    this.tapTogglesChrome = sheetTop === null;
    this.apply(true);
  }

  /** Pointer moved (stage px y, or null when outside the stage). */
  pointerMoved(stageY: number | null, nowMs: number): void {
    this.lastMove = nowMs;
    this.nearEdge = stageY !== null && stageY > this.stageH - EDGE_ZONE_STAGE_PX;
  }

  pointerLeft(): void {
    this.nearEdge = false;
  }

  toggleHidden(): void {
    this.hidden = !this.hidden;
    this.apply(true);
  }

  /** T2: a touch tap on the map that hit no car and no HUD region toggles the bar, like H.
   *  Portrait turns this off (its transport is always visible) by setting the flag false. */
  tapTogglesChrome = true;

  tapToggle(): void {
    if (!this.tapTogglesChrome) return;
    this.toggleHidden();
    // a touch tap fires no pointermove: refresh the reveal timer, or the auto-hide would
    // swallow the show while playing
    if (!this.hidden) this.lastMove = performance.now();
  }

  /** Per frame: auto-hide. */
  tick(nowMs: number, playing: boolean, busy: boolean): void {
    const want = this.portrait || !playing || busy || this.hold > 0 || this.nearEdge || nowMs - this.lastMove < AUTO_HIDE_MS;
    if (want !== this.visible) {
      this.visible = want;
      this.apply(false);
    }
  }

  private apply(force: boolean): void {
    const show = this.visible && !this.hidden;
    if (!force && this.root.dataset.chromeVisible === (show ? '1' : '0')) return;
    this.root.dataset.chromeVisible = show ? '1' : '0';
    this.root.dataset.chromeHidden = this.hidden ? '1' : '0';
  }
}
